<template>
  <div class="container">
    <div ref="descRef" class="show-more" :style="contentStyle">
      <slot />
    </div>
    <Button
      v-if="shouldShowButton"
      type="link"
      variant="black"
      @click.prevent.stop="toggleViewMore()"
      class="!h-min no-underline relative z-10 dark:!text-zinc-100 dark:hover:!bg-zinc-600"
      :class="buttonClass"
      >{{ isExpanded ? 'View less' : 'View more' }}</Button
    >
  </div>
</template>
<script lang="ts" setup>
export interface Props {
  linesToShow?: number;
  buttonClass?: string;
}

const props = withDefaults(defineProps<Props>(), { linesToShow: 12, buttonClass: '' });

import { useResizeObserver } from '@vueuse/core';
import { ref, Ref, computed, onMounted, nextTick, onUnmounted } from 'vue';
import Button from '../button/Button.vue';

const descRef: Ref<HTMLElement | null> = ref(null);
const fullContentHeight: Ref<number | null> = ref(null);
const clampHeight: Ref<number | null> = ref(null);
const isExpanded: Ref<boolean> = ref(false);
const shouldClamp: Ref<boolean> = ref(false);

let measurementDone = false;
let isToggling = false;
let observerInstance: any;

const shouldShowButton = computed(() => shouldClamp.value);

const contentStyle = computed(() => {
  if (!shouldClamp.value) {
    return { maxHeight: 'none', overflow: 'visible' };
  }

  if (isExpanded.value) {
    return {
      maxHeight: 'none',
      overflow: 'visible',
      transition: 'max-height 0.3s ease',
    };
  }

  return {
    maxHeight: clampHeight.value ? `${clampHeight.value}px` : 'none',
    overflow: 'hidden',
    transition: 'max-height 0.3s ease',
  };
});

const measureContent = () => {
  if (!descRef.value || measurementDone) {
    return;
  }

  const el = descRef.value;

  // Temporarily remove any constraints to measure natural height
  const prevMaxHeight = el.style.maxHeight;
  const prevOverflow = el.style.overflow;
  el.style.maxHeight = 'none';
  el.style.overflow = 'visible';

  // Measure full content height
  fullContentHeight.value = el.scrollHeight;

  // Get computed line height
  const computedStyle = window.getComputedStyle(el);
  const lineHeight = parseFloat(computedStyle.lineHeight);

  // Calculate clamp height based on linesToShow
  if (!isNaN(lineHeight) && lineHeight > 0) {
    clampHeight.value = Math.ceil(lineHeight * props.linesToShow);
  } else {
    // Fallback: estimate line height from font size
    const fontSize = parseFloat(computedStyle.fontSize);
    const estimatedLineHeight = fontSize * 1.5; // typical line-height multiplier
    clampHeight.value = Math.ceil(estimatedLineHeight * props.linesToShow);
  }

  // Determine if content needs clamping
  if (fullContentHeight.value && clampHeight.value) {
    shouldClamp.value = fullContentHeight.value > clampHeight.value;

    // Start in clamped state if content exceeds limit
    if (shouldClamp.value) {
      isExpanded.value = false;
    }
  }

  // Restore original styles
  el.style.maxHeight = prevMaxHeight;
  el.style.overflow = prevOverflow;

  measurementDone = true;
};

const observe = () => {
  const { stop } = useResizeObserver(descRef, () => {
    // Don't re-measure during toggle animations
    if (isToggling) {
      return;
    }

    // Re-measure on resize or content changes
    measurementDone = false;

    nextTick(() => {
      // Small delay to allow for dynamic content (like Screenshot components) to mount
      setTimeout(() => {
        measureContent();
      }, 100);
    });
  });

  return { stop };
};

onMounted(() => {
  if (!descRef.value) {
    return;
  }

  observerInstance = observe();

  // Initial measurement with delay for dynamic content
  nextTick(() => {
    setTimeout(() => {
      measureContent();
    }, 100);
  });
});

onUnmounted(() => observerInstance && observerInstance.stop());

const toggleViewMore = () => {
  isToggling = true;
  isExpanded.value = !isExpanded.value;

  // Reset toggling flag after animation completes
  setTimeout(() => {
    isToggling = false;
  }, 350); // Slightly longer than the 0.3s transition
};
</script>
<style scoped>
.container {
  @apply flex flex-col;
}

.show-more {
  @apply mb-1;
}
</style>
